import { useState, useEffect, useCallback } from "react";
import BackMethods from "../../back_component/BackMethods";
import { useNavigate, useLocation } from "react-router-dom";
import { FaArrowRight, FaUsers } from "react-icons/fa";
import "../../styles/admin-shared.css";
import "./users-admin.css";
import UserManagementModals from "./UserManagementModals";

/** ترقيم ذكي: أرقام الصفحات مع نقاط حذف عند وجود عدد كبير من الصفحات */
function buildPaginationItems(currentPage, totalPages, siblingCount = 2, boundaryCount = 1) {
    if (totalPages < 1) return [];
    const maxAll = 10;
    if (totalPages <= maxAll) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const set = new Set();
    for (let i = 1; i <= Math.min(boundaryCount, totalPages); i += 1) set.add(i);
    for (let i = Math.max(1, totalPages - boundaryCount + 1); i <= totalPages; i += 1) set.add(i);
    for (let i = currentPage - siblingCount; i <= currentPage + siblingCount; i += 1) {
        if (i >= 1 && i <= totalPages) set.add(i);
    }
    const sorted = Array.from(set).sort((a, b) => a - b);
    const out = [];
    let prev = 0;
    for (const p of sorted) {
        if (prev && p - prev > 1) out.push("ellipsis");
        out.push(p);
        prev = p;
    }
    return out;
}

export default function UsersManagementPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { backEnd_request } = BackMethods();

    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [usersPerPage, setUsersPerPage] = useState(10);
    const [flash, setFlash] = useState(null);

    const [modal, setModal] = useState(null);
    const [modalUserId, setModalUserId] = useState(null);
    const [passwordUsername, setPasswordUsername] = useState("");

    const fetchUsers = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await backEnd_request.get("/admin/UsersManagement", {
                params: {
                    SearchTerm: searchTerm,
                    Page: currentPage,
                    PageSize: usersPerPage
                }
            });
            setUsers(res.data.users || []);
            setTotalCount(res.data.totalCount || 0);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm, currentPage, usersPerPage, backEnd_request]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => fetchUsers(), 400);
        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm, currentPage, usersPerPage, fetchUsers]);

    useEffect(() => {
        if (location.state?.successMessage) {
            setFlash({ message: location.state.successMessage, variant: "success" });
            navigate(".", { replace: true, state: {} });
        }
    }, [location.state, navigate]);

    const totalPages = Math.ceil(totalCount / usersPerPage);
    const paginationItems = totalPages > 1 ? buildPaginationItems(currentPage, totalPages) : [];

    const paginate = (n) => {
        setCurrentPage(n);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleDelete = async (userId) => {
        if (window.confirm("هل أنت متأكد من حذف هذا المستخدم؟")) {
            try {
                await backEnd_request.delete("/admin/UsersManagement/" + userId);
                fetchUsers();
            } catch (error) { console.error(error); }
        }
    };

    const handleToggleActive = async (userId, isActive) => {
        const willBeActive = !isActive;
        const confirmText = willBeActive
            ? "هل تريد تفعيل هذا الحساب؟ سيتمكن المستخدم من تسجيل الدخول."
            : "هل تريد تعطيل هذا الحساب؟ لن يتمكن المستخدم من تسجيل الدخول.";
        if (!window.confirm(confirmText)) return;
        try {
            await backEnd_request.patch("/admin/UsersManagement/" + userId + "/toggle-active", { isActive: willBeActive });
            await fetchUsers();
            setFlash({
                message: willBeActive ? "تم تفعيل الحساب بنجاح." : "تم تعطيل الحساب بنجاح.",
                variant: "success",
            });
        } catch (error) {
            console.error(error);
            setFlash({
                message: error.response?.data?.message || "تعذر تغيير حالة الحساب. حاول مرة أخرى.",
                variant: "danger",
            });
        }
    };

    const closeModal = () => {
        setModal(null);
        setModalUserId(null);
        setPasswordUsername("");
    };

    const onModalSaved = () => {
        fetchUsers();
        setFlash({ message: "تم حفظ التغييرات بنجاح", variant: "success" });
    };

    useEffect(() => {
        if (!flash?.message) return undefined;
        const t = setTimeout(() => setFlash(null), 4500);
        return () => clearTimeout(t);
    }, [flash]);

    const openCreate = () => setModal("create");
    const openEdit = (userId) => {
        setModalUserId(userId);
        setModal("edit");
    };
    const openPassword = (userId, username) => {
        setModalUserId(userId);
        setPasswordUsername(username || "");
        setModal("password");
    };

    return (
        <div className="users-page-minimal users-admin-page " style={{ direction: "rtl", fontFamily: "Cairo, sans-serif" }}>
            <div className="container pb-5 pt-4 px-3 bg-white">

                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 pb-3 border-bottom gap-3">
                    <div>
                        <h3 className="fw-bold text-primary mb-1 d-flex align-items-center gap-2">
                            <span className="rounded-3 bg-primary bg-opacity-10 p-2 d-inline-flex text-primary">
                                <FaUsers />
                            </span>
                            إدارة المستخدمين
                        </h3>
                        <p className="text-muted small mb-0">إدارة الحسابات والصلاحيات من مكان واحد</p>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <button type="button" className="btn btn-primary d-flex align-items-center px-4 rounded-pill shadow-sm" onClick={openCreate}>
                            <i className="bi bi-plus-lg ms-2"></i> إضافة مستخدم
                        </button>
                        <button type="button" onClick={() => navigate("/admin")} className="btn btn-outline-secondary rounded-pill">
                            <FaArrowRight className="ms-1" /> رجوع
                        </button>
                    </div>
                </div>

                {flash?.message ? (
                    <div
                        className={`users-admin-alert px-3 py-2 mb-3 d-flex align-items-center justify-content-between gap-2 ${flash.variant === "danger" ? "users-admin-alert--danger" : ""}`}
                        role="alert"
                    >
                        <span>{flash.message}</span>
                        <button
                            type="button"
                            className={`btn btn-sm btn-link p-0 ${flash.variant === "danger" ? "text-danger" : "text-success"}`}
                            onClick={() => setFlash(null)}
                        >
                            إغلاق
                        </button>
                    </div>
                ) : null}

                <div className="users-toolbar mb-3 bg-light  rounded-3 border">
                    <div className="users-count-chip">
                        إجمالي الحسابات
                        <span>{totalCount}</span>
                    </div>
                    <div className="users-search-wrap">
                        <div className="bg-white border rounded-4 p-2 shadow-sm">
                            <div className="input-group border-0">
                                <span className="input-group-text bg-transparent border-0 text-muted"><i className="bi bi-search"></i></span>
                                <input
                                    type="text"
                                    className="form-control border-0 shadow-none py-2"
                                    placeholder="ابحث عن اسم أو حساب..."
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {isLoading ? (
                    <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-secondary"></div></div>
                ) : (
                    <>
                        <div className="users-table-shell">
                            {users.length === 0 ? (
                                <div className="empty-state">
                                    <i className="bi bi-inbox fs-4 mb-2"></i>
                                    <div>لا يوجد مستخدمون مطابقون للبحث</div>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table users-table align-middle mb-0 users-table--users">
                                        <thead>
                                            <tr>
                                                <th className="text-center col-index">#</th>
                                                <th className="text-end">اسم المستخدم</th>
                                                <th className="text-center">المحافظة</th>
                                                <th className="text-center">الصلاحية</th>
                                                <th className="text-center">الحالة</th>
                                                <th className="text-center">الإجراءات</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user, index) => (
                                                <tr key={user.userId}>
                                                    <td className="text-center col-index fw-semibold text-muted">
                                                        {(currentPage - 1) * usersPerPage + index + 1}
                                                    </td>
                                                    <td className="text-end">
                                                        <span className="user-name">{user.username}</span>
                                                    </td>
                                                    <td className="text-center">{user.governorate || "-"}</td>
                                                    <td className="text-center">
                                                        <span className="role-pill">{user.roleDisplayName || user.roleName || "-"}</span>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className={`status-pill ${user.isActive ? "status-active" : "status-inactive"}`}>
                                                            {user.isActive ? "نشط" : "معطل"}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        <div className="d-flex gap-2 justify-content-center flex-wrap">
                                                            <button type="button" className="btn-minimal" title="تعديل" onClick={() => openEdit(user.userId)}><i className="bi bi-pencil"></i></button>
                                                            <button type="button" className="btn-minimal" title="كلمة المرور" onClick={() => openPassword(user.userId, user.username)}><i className="bi bi-key"></i></button>
                                                            <button type="button" className={`btn-minimal ${user.isActive ? "active" : ""}`} title="تغيير الحالة" onClick={() => handleToggleActive(user.userId, user.isActive)}><i className={`bi ${user.isActive ? "bi-unlock" : "bi-lock"}`}></i></button>
                                                            <button type="button" className="btn-minimal text-danger-hover" title="حذف" onClick={() => handleDelete(user.userId)}><i className="bi bi-trash3"></i></button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className="admin-table-footer d-flex flex-wrap justify-content-between align-items-stretch align-items-md-center gap-3 mt-4">
                            <div className="admin-page-size">
                                <span className="admin-page-size__icon" aria-hidden>
                                    <i className="bi bi-layout-three-columns" />
                                </span>
                                <label htmlFor="users-page-size" className="admin-page-size__label">عناصر الصفحة</label>
                                <div className="admin-page-size__select-wrap">
                                    <select
                                        id="users-page-size"
                                        className="admin-page-size__select"
                                        value={usersPerPage}
                                        onChange={(e) => {
                                            setUsersPerPage(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={30}>30</option>
                                    </select>
                                </div>
                            </div>

                            {totalPages > 1 ? (
                                <nav className="admin-pagination" aria-label="ترقيم الصفحات">
                                    <span className="admin-pagination__summary">
                                        صفحة <strong>{currentPage}</strong> من <strong>{totalPages}</strong>
                                    </span>
                                    <div className="minimal-pagination admin-pagination__bar">
                                        <button
                                            type="button"
                                            className="page-arrow"
                                            disabled={currentPage === 1}
                                            onClick={() => paginate(currentPage - 1)}
                                            aria-label="الصفحة السابقة"
                                        >
                                            <i className="bi bi-chevron-right" />
                                        </button>
                                        <div className="page-numbers">
                                            {paginationItems.map((item, idx) =>
                                                item === "ellipsis" ? (
                                                    <span key={`ellipsis-${idx}`} className="page-ellipsis">
                                                        …
                                                    </span>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        key={item}
                                                        className={`page-dot ${currentPage === item ? "active" : ""}`}
                                                        onClick={() => paginate(item)}
                                                        aria-label={`الصفحة ${item}`}
                                                        aria-current={currentPage === item ? "page" : undefined}
                                                    >
                                                        {item}
                                                    </button>
                                                )
                                            )}
                                        </div>
                                        <button
                                            type="button"
                                            className="page-arrow"
                                            disabled={currentPage === totalPages}
                                            onClick={() => paginate(currentPage + 1)}
                                            aria-label="الصفحة التالية"
                                        >
                                            <i className="bi bi-chevron-left" />
                                        </button>
                                    </div>
                                </nav>
                            ) : null}
                        </div>
                    </>
                )}
            </div>

            <UserManagementModals
                modal={modal}
                userId={modalUserId}
                passwordUsername={passwordUsername}
                onClose={closeModal}
                onSaved={onModalSaved}
            />
        </div>
    );
}
